/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React from 'react';
import './Tour.css';
import Tour, { TourStep } from './Tour';
import Monster from 'model/monster/Monster';

// A standalone tour for the 2024 Monster Manual style, launched from the '?' icon next to the
// 'Use MM2024 style' checkbox. Moved out of the main tour. The first step just ensures the style is on
// (so it works whether or not the user already enabled it), rather than relying on the main tour's
// tourState signal.
interface TourMM2024Props {
    currentStepIndex: number;
    mon: Monster;
    openBlock: string;
    openIndex: number;
    showAll: boolean;
    setCurrentStepIndex: (step: number) => void;
    onClose: (completed?: boolean) => void;
}

const TourMM2024: React.FC<TourMM2024Props> = ({ currentStepIndex, mon, openBlock, openIndex, showAll,
                                                setCurrentStepIndex, onClose }) => {
    const tourSteps: TourStep[] = [{
        highlightElement: '#showUseMm2024',
        title: 'Monster Manual 2024 Style',
        scrollIntoView: true,
        description: (<div>
            This tour shows the style and layout of the new Monster Manual 2024.
            There are a few difference in how things work between the two styles.<br/>
            All the monsters from the "5e 2024 Rules" group use the MM2024 style by default.
            Monsters shared by the community might use either.
            <div className='user-tour-action'>Check the 'Use MM2024 style' checkbox.</div>
        </div>),
        testToContinue: () => mon.useMm2024,
        position: 'top'
    }, {
        highlightElement: '#showUseMm2024',
        title: 'Monster Manual 2024 Style',
        scrollIntoView: true,
        description: (<div>
            This tour shows the style and layout of the new Monster Manual 2024.
            There are a few difference in how things work between the two styles.<br/>
            All the monsters from the "5e 2024 Rules" group use the MM2024 style by default.
            Monsters shared by the community might use either.
            <div className='user-tour-action'>Click <button className='fake-button'>Next</button>.</div>
        </div>),
        position: 'top'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Monster Manual 2024 Style',
        scrollIntoView: true,
        description: (<div>
            While in the MM2024 style, initiative is shown a skill, but is displayed next to AC.
            <div className='user-tour-action'>Click on the initiative field</div>
        </div>),
        testToContinue: () => openBlock === 'skills',
        position: 'left'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Monster Manual 2024 Style',
        scrollIntoView: true,
        description: (<div>
            You see this opens up the normal skills selector, same as if you'd clicked on the skills section.
            While in MM2024 mode, this skill wont show in the normal skills list area, but if you switch back to the legacy view, you will see it with the other skills.
            <div className='user-tour-action'>Give this monster proficiency in the Initiative skill. Then close the skills selector.</div>
        </div>),
        testToContinue: () => mon.skillSet.skills['Initiative']?.level === 'proficient' && openIndex === -1,
        position: 'left'
    }, {
        highlightElement: '#showEmptyBlocks',
        title: 'MM2024 Immunities',
        scrollIntoView: true,
        description: (<div>
            The Damage and Condition Immunities have been combined into one area, but are still 2 separate controls.
            First, you'll have to show the empty blocks again.
            <div className='user-tour-action'>Click the 'Show Empty Blocks' checkbox.</div>
        </div>),
        testToContinue: () => showAll,
        position: 'right'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'MM2024 Immunities',
        scrollIntoView: true,
        description: (<div>
            While the 'Show Empty Blocks' is on, you'll see both immunity sections next to each other ({mon.damage_immunities || '--'} , {mon.condition_immunities || '--'})
            <div className='user-tour-action'>Add imminutity to the 'fire' damage type, and the 'petrified' condition.
                                              Then close both immunity controls</div>
        </div>),
        testToContinue: () => mon.damage_immunities.includes('fire') && mon.condition_immunities.includes('petrified') && openIndex === -1,
        position: 'left'
    }, {
        highlightElement: '#stat-block-wrapper',
        title: 'Monster Manual 2024 Style',
        scrollIntoView: true,
        description: (<div>
            Saving throws are now adjusted by clicking on the new saving throw field, next to each attribute.
            <div className='user-tour-action'>Cick on the saving throw entry for the Strength attribute to give your monster proficiency in STR saves.</div>
        </div>),
        testToContinue: () => mon.saving_throws.toLowerCase().includes('str'),
        position: 'left'
    }, {
        highlightElement: '#gearLine',
        title: 'Monster Manual 2024 Style',
        scrollIntoView: true,
        description: (<div>
            Gear is a new field when viewing in MM2024 style.
            As you change your armor, or add/remove actions that correspond to simple weapons, this field will update.
            In fact, this is now the only place you'll see your current armor/shield on the main sheet.
            Magic items from the 'Inventory' section will also be shown here.
            <br/><br/>That's the end of the MM2024 style tour!
        </div>),
        position: 'left'
    }];

    if (currentStepIndex >= tourSteps.length) {
        return null;
    }
    return <Tour steps={tourSteps} currentStepIndex={currentStepIndex} setCurrentStepIndex={setCurrentStepIndex} onClose={onClose} tourId="mm2024" />;
};

export default TourMM2024;
